import React, { useState } from "react";
import jsPDF from "jspdf";
import "./LunchRecipeDetails.css";
import { images } from "../../assets/images";
import { Flame } from "lucide-react";

// Clock icon SVG
const clockIcon = images.clockIcon;

const kitchenIcon = images.kitchenIcon;
// Divider SVG
const dividerIcon =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='59' viewBox='0 0 1 59' fill='none' stroke='%23FFFFFF' stroke-width='1'%3E%3Cline x1='0' y1='0' x2='0' y2='59'%3E%3C/line%3E%3C/svg%3E";

// Copy icon SVG
const copyIcon =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23FFFFFF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2'%3E%3C/path%3E%3Cpath d='M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z'%3E%3C/path%3E%3C/svg%3E";

// PDF icon SVG
const pdfIcon =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23FFFFFF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'%3E%3C/path%3E%3Cpolyline points='14 2 14 8 20 8'%3E%3C/polyline%3E%3Ctext x='8' y='13' font-size='6' font-weight='bold'%3EPDF%3C/text%3E%3C/svg%3E";

// Helper function to convert ALL CAPS to Title Case
const toTitleCase = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Helper function to split text into sentences
const splitSentences = (text) => {
  return String(text)
    .split(/(?<=[.!?])\s+(?=[A-Z0-9])/)
    .map((s) => s.trim())
    .filter(Boolean);
};
function LunchRecipeDetails({ recipe, onSelect, mealTime }) {
  const [isOpen, setIsOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  if (mealTime === "evening") {
    mealTime = "Iftar";
  } else {
    mealTime = "Dinner";
  }
  const {
    recipe_name,
    servings,
    cooking_time,
    ingredients,
    preparation,
    category,
  } = recipe;

  // Generate markdown format
  const generateMarkdown = () => {
    let markdown = `# ${recipe_name}\n\n`;
    markdown += `**Cooking Time:** ${cooking_time} | **Serves:** ${servings}\n\n`;

    markdown += `## Ingredients\n\n`;
    if (ingredients && ingredients.length > 0) {
      ingredients.forEach((group) => {
        if (group.section_title) {
          markdown += `### ${group.section_title}\n`;
        }
        if (group.items) {
          group.items.forEach((item) => {
            markdown += `- ${toTitleCase(item.name)} - ${item.quantity || ""}\n`;
          });
        }
        markdown += `\n`;
      });
    }

    markdown += `## Directions\n\n`;
    if (preparation && preparation.length > 0) {
      preparation.forEach((step, idx) => {
        const stepNumber = step.step_id ?? idx + 1;
        const title = (step.step_title || "").trim();
        markdown += `### Step ${stepNumber}: ${title}\n`;

        if (Array.isArray(step.instructions)) {
          step.instructions.forEach((instruction) => {
            const sentences = splitSentences(instruction);
            sentences.forEach((sentence) => {
              markdown += `- ${sentence}\n`;
            });
          });
        }
        markdown += `\n`;
      });
    }

    return markdown;
  };

  // Copy to clipboard
  const handleCopyMarkdown = async () => {
    try {
      const markdown = generateMarkdown();
      await navigator.clipboard.writeText(markdown);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Export to PDF using native jsPDF text rendering (no screenshot)
  const handleExportPDF = async () => {
    try {
      setExportLoading(true);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pageW = 210;
      const pageH = 297;
      const margin = 20;
      const contentW = pageW - margin * 2;
      let y = margin;

      // Colors
      const green = [0, 122, 51];
      const yellow = [255, 178, 2];
      const white = [255, 255, 255];
      const lightGreen = [230, 245, 235];

      // Helper: check remaining space, add new page if needed
      const ensureSpace = (needed) => {
        if (y + needed > pageH - margin) {
          pdf.addPage();
          // Green background on new page
          pdf.setFillColor(...green);
          pdf.rect(0, 0, pageW, pageH, "F");
          y = margin;
        }
      };

      // Helper: wrap and render text, returns new y
      const renderText = (
        text,
        x,
        fontSize,
        color,
        style = "normal",
        maxWidth = contentW,
      ) => {
        pdf.setFontSize(fontSize);
        pdf.setFont("helvetica", style);
        pdf.setTextColor(...color);
        const lines = pdf.splitTextToSize(text, maxWidth);
        const lineH = fontSize * 0.45;
        for (let i = 0; i < lines.length; i++) {
          ensureSpace(lineH);
          pdf.text(lines[i], x, y);
          y += lineH;
        }
        return y;
      };

      // Helper: draw a horizontal divider
      const drawDivider = (color = yellow, thickness = 0.5) => {
        ensureSpace(6);
        y += 3;
        pdf.setDrawColor(...color);
        pdf.setLineWidth(thickness);
        pdf.line(margin, y, pageW - margin, y);
        y += 3;
      };

      // --- Page background ---
      pdf.setFillColor(...green);
      pdf.rect(0, 0, pageW, pageH, "F");

      // --- Title ---
      renderText(recipe_name, margin, 22, yellow, "bold");
      y += 4;

      // --- Meta info row ---
      const metaText = `${cooking_time}  |  Serves ${servings}`;
      renderText(metaText, margin, 11, white, "normal");
      y += 2;

      // === INGREDIENTS ===
      y += 4;
      renderText("Ingredients", margin, 16, yellow, "bold");
      drawDivider();
      y += 2;

      if (ingredients && ingredients.length > 0) {
        ingredients.forEach((group) => {
          if (group.section_title) {
            ensureSpace(14);
            y += 2;
            // Section title with left accent bar
            const barY = y - 4;
            pdf.setFillColor(...yellow);
            pdf.rect(margin, barY, 2, 6, "F");
            renderText(group.section_title, margin + 6, 12, white, "bold");
            y += 1;
          }
          if (group.items) {
            group.items.forEach((item) => {
              ensureSpace(7);
              const name = toTitleCase(item.name);
              const qty = item.quantity || "";
              pdf.setFontSize(10);
              pdf.setFont("helvetica", "normal");
              pdf.setTextColor(...white);
              pdf.text(name, margin + 4, y);
              if (qty) {
                pdf.setTextColor(...yellow);
                pdf.text(qty, pageW - margin, y, { align: "right" });
              }
              y += 6;
            });
          }
          y += 2;
        });
      } else {
        renderText("No ingredients listed", margin + 4, 10, white);
        y += 4;
      }

      // === DIRECTIONS ===
      y += 4;
      renderText("Directions", margin, 16, yellow, "bold");
      drawDivider();
      y += 2;

      if (preparation && preparation.length > 0) {
        preparation.forEach((step, idx) => {
          const stepNumber = step.step_id ?? idx + 1;
          const title = (step.step_title || "").trim();

          // Step heading with accent bar
          ensureSpace(14);
          y += 2;
          const barY = y - 4;
          pdf.setFillColor(...yellow);
          pdf.rect(margin, barY, 2, 6, "F");
          renderText(
            `Step ${stepNumber}: ${title}`,
            margin + 6,
            12,
            white,
            "bold",
          );
          y += 1;

          const rows = Array.isArray(step.instructions)
            ? step.instructions
                .filter(Boolean)
                .flatMap((p) => splitSentences(p))
            : [];

          if (rows.length > 0) {
            rows.forEach((sentence) => {
              renderText(
                `•  ${sentence}`,
                margin + 6,
                10,
                white,
                "normal",
                contentW - 8,
              );
              y += 1;
            });
          } else {
            renderText(
              "No instructions provided",
              margin + 6,
              10,
              [...white],
              "italic",
            );
          }
          y += 4;
        });
      } else {
        renderText("No directions available", margin + 4, 10, white);
      }

      // --- Footer on last page ---
      ensureSpace(15);
      y += 5;
      drawDivider(white, 0.3);
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "italic");
      pdf.setTextColor(200, 200, 200);
      pdf.text("Generated by Knorr Recipe AI", pageW / 2, y, {
        align: "center",
      });

      // --- Save ---
      const fileName = recipe_name.replace(/[^a-z0-9]/gi, "_") + ".pdf";
      const pdfBlob = pdf.output("blob");
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(pdfBlob);
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);
    } catch (err) {
      console.error("Failed to export PDF:", err);
      alert("Failed to export PDF. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };
  

  return (
    <>
      {/* Closed State Header */}
      {!isOpen && (
        <div className="recipe-card-header-rd">
          <div className="recipe-header-content-rd">
            <h2 className="recipe-card-h1-rd">{recipe_name}</h2>
            <div className="recipe-meta-section-rd">
              
              <div className="time-badge-rd">
                <img src={clockIcon} alt="" className="time-icon-rd" />
                <span className="time-text-rd">{cooking_time}</span>
              </div>
              
              <div className="time-badge-rd">
                <img src={kitchenIcon} alt="" className="time-icon-rd" />
                <span className="time-text-rd">Serves {servings}</span>
              </div>
            </div>
            <div className="recipe-action-buttons-rd">
              <button
                className="copy-button-rd"
                onClick={handleCopyMarkdown}
                title="Copy as markdown"
                type="button"
                aria-label="Copy Button"
              >
                <img src={copyIcon} alt="Copy" />
              </button>
              <button
                className="pdf-button-rd"
                onClick={handleExportPDF}
                disabled={exportLoading}
                title="Export as PDF"
                type="button"
                aria-label="Download PDF"
                data-ga-name="Export PDF"
                data-ga-type="download"
              >
                <img src={pdfIcon} alt="PDF" />
              </button>

              <button
                className="view-recipe-button-rd"
                onClick={() => setIsOpen(true)}
              >
                View recipe
              </button>
            </div>
            {copySuccess && <span className="copied-tooltip-rd">Copied!</span>}
          </div>
        </div>
      )}

      {/* Open State - Full Recipe */}
      {isOpen && (
        <div className="recipe-card-expanded-rd">
          <div className="recipe-header-content-rd">
            <h2 className="recipe-card-h1-rd">{recipe_name}</h2>
            <div className="recipe-meta-section-rd">
              <div className="time-badge-rd">
                <img src={clockIcon} alt="" className="time-icon-rd" />
                <span className="time-text-rd">{cooking_time}</span>
              </div>
              
              <div className="time-badge-rd">
                <img src={kitchenIcon} alt="" className="time-icon-rd" />
                <span className="time-text-rd">Serves {servings}</span>
              </div>
            </div>
            <div className="recipe-action-buttons-rd">
              <button
                className="copy-button-rd"
                onClick={handleCopyMarkdown}
                title="Copy as markdown"
                type="button"
              >
                <img src={copyIcon} alt="Copy" />
              </button>
              <button
                className="pdf-button-rd"
                onClick={handleExportPDF}
                disabled={exportLoading}
                title="Export as PDF"
                type="button"
              >
                <img src={pdfIcon} alt="PDF" />
              </button>
              <button
                className="view-recipe-button-rd"
                onClick={() => setIsOpen(false)}
              >
                Hide recipe
              </button>
            </div>
            {copySuccess && <span className="copied-tooltip-rd">Copied!</span>}
          </div>

          <div className="recipe-divider-rd"></div>

          {/* Ingredients Section */}
          <div className="recipe-section-rd">
            <h3 className="section-title-rd">Ingredients:</h3>
            <div className="ingredients-container-rd">
              {ingredients && ingredients.length > 0 ? (
                ingredients.map((ingredientGroup, idx) => (
                  <div key={idx} className="ingredient-section-group-rd">
                    {ingredientGroup.section_title && (
                      <h4 className="ingredient-section-title-rd">
                        {ingredientGroup.section_title}
                      </h4>
                    )}
                    <div className="ingredient-items-grid-rd">
                      {ingredientGroup.items &&
                        ingredientGroup.items.map((item, itemIdx) => (
                          <div key={itemIdx} className="ingredient-row-rd">
                            <span className="ingredient-name-rd">
                              {toTitleCase(item.name)}
                            </span>
                            <span className="ingredient-dots-rd"></span>
                            <span className="ingredient-quantity-rd">
                              {item.quantity || ""}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                ))
              ) : (
                <p className="ingredient-item-rd">No ingredients listed</p>
              )}
            </div>
          </div>

          <div className="recipe-divider-rd"></div>

          {/* Directions Section */}
          <div className="recipe-section-rd">
            <h3 className="section-title-rd">Directions:</h3>

            <div className="ingredients-container-rd">
              {preparation && preparation.length > 0 ? (
                preparation.map((step, idx) => {
                  const stepNumber = step.step_id ?? idx + 1;
                  const title = (step.step_title || "").trim();

                  // Normalize: instructions MUST be an array; then split EACH item into sentences
                  const rows = Array.isArray(step.instructions)
                    ? step.instructions
                        .filter(Boolean)
                        .flatMap((paragraph) => splitSentences(paragraph))
                    : []; // ignore non-array per your rule

                  return (
                    <div
                      key={step.step_id ?? `step-${idx}`}
                      className="ingredient-section-group-rd"
                    >
                      <h4 className="ingredient-section-title-rd">
                        {`Step ${stepNumber}: ${title}`}
                      </h4>

                      <div className="ingredient-items-grid-rd">
                        {rows.length > 0 ? (
                          rows.map((sentence, sIdx) => (
                            <div key={sIdx} className="ingredient-row-rd">
                              <span className="ingredient-name-rd">
                                • {sentence}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="ingredient-row-rd">
                            <span className="ingredient-name-rd">
                              No instructions provided
                            </span>
                            <span className="ingredient-dots-rd"></span>
                            <span className="ingredient-quantity-rd"></span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="ingredient-section-group-rd">
                  <h4 className="ingredient-section-title-rd">Directions</h4>
                  <div className="ingredient-items-grid-rd">
                    <div className="ingredient-row-rd">
                      <span className="ingredient-name-rd">
                        No directions available
                      </span>
                      <span className="ingredient-dots-rd"></span>
                      <span className="ingredient-quantity-rd"></span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default LunchRecipeDetails;
